import { LABELS_CRUD_ACTION_CREATORS } from '../actions/labels_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../utils/DataConstants';
import {
	createLabelCatalogueSyncThunk,
	getDeletedLabelIds,
	LabelCatalogueEntry,
} from './label_catalogue_sync';
import { nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { buildLabelFilterRemovalPatch } from '../utils/FilterUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { partsRestCrudThunksForBuyer, partsRestCrudThunksForSupplier } from './parts_thunks';
import { PARTS_CRUD_ACTION_CREATORS } from '../actions/parts_actions';
import { getRecordsForTargetCollection } from '../reducers/standard_reducer_utils';
import { LabelEntityType } from '../types/labels';

export const labelsOnPartsRestCrudThunksForSupplier = new RestCrudThunks(
	'part_labels',
	'label',
	'label',
	'part_labels',
	LABELS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'inventory/'
);

const fetchUtils = new FetchUtils();

export const associateLabelsWithEntity =
	(roleType: string) => (entityId: number, labelIds: number[]) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/inventory/parts/${entityId}/labels`;

		return (dispatch) => {
			return new Promise<any>((resolve, reject) => {
				fetchUtils
					.put(baseUrl + apiUrl, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: { ids: labelIds },
					})
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
						} else {
							// Refetch the part so row and detail chips pick up server-truth labels.
							dispatch(
								(roleType === ROLE_TYPES.SUPPLIER
									? partsRestCrudThunksForSupplier
									: partsRestCrudThunksForBuyer
								).readOne(entityId)
							)
								.catch(() => undefined)
								.then(() => resolve(d.data));
						}
					})
					.catch((error) => {
						console.error('Error associating labels:', error);
						reject(error);
					});
			});
		};
	};

// Cached parts keep their own copy of the labels applied to them, so a deleted label lingers
// on the row until a page reload. Drop it from every cached record.
export const removeDeletedLabelsFromParts = (deletedLabelIds: number[]) => (dispatch, getState) => {
	const deleted = new Set(deletedLabelIds.map(Number));
	const slice = getState().parts;
	const patched = new Set();
	[slice.records, slice.searchRecords].forEach((recordMap) => {
		Object.keys(recordMap || {}).forEach((recordId) => {
			if (patched.has(recordId)) return;
			const labels = recordMap[recordId] && recordMap[recordId].partLabels;
			if (!Array.isArray(labels)) return;
			const kept = labels.filter((label) => !deleted.has(Number(label && label.id)));
			if (kept.length === labels.length) return;
			patched.add(recordId);
			dispatch(
				PARTS_CRUD_ACTION_CREATORS.updatePartialSuccess({ id: Number(recordId), partLabels: kept })
			);
		});
	});
};

const baseSyncPartLabelsForRole = createLabelCatalogueSyncThunk(
	(roleType) => `/api/v1/${roleType}/inventory/part_labels/sync`,
	'Failed to save labels. Please try again.',
	{
		getCatalogue: (state) => getRecordsForTargetCollection(state.labels, LabelEntityType.PART),
		onDeletedLabels: removeDeletedLabelsFromParts,
	}
);

// Deleting a label must also unpin it from an active Labels filter, or the list stays filtered
// on an id that matches nothing. updateFilters merges, so an undefined value clears the key.
const syncPartLabelsForRole =
	(roleType: string) =>
	(labels: LabelCatalogueEntry[], context: { targetCollectionName?: string } = {}) =>
	(dispatch, getState) => {
		const catalogueBeforeSync = getRecordsForTargetCollection(
			getState().labels,
			LabelEntityType.PART
		);
		return dispatch(baseSyncPartLabelsForRole(roleType)(labels)).then((result) => {
			const { targetCollectionName } = context;
			if (!targetCollectionName) return result;
			const deletedLabelIds = getDeletedLabelIds(catalogueBeforeSync, labels);
			const filters = nullSafeGetOrElse(`${targetCollectionName}.filters`, getState().parts, {});
			const patch = buildLabelFilterRemovalPatch(
				filters,
				deletedLabelIds,
				FILTER_CONFIG_NAMES.PART_LABELS
			);
			if (patch) dispatch(PARTS_CRUD_ACTION_CREATORS.updateFilters(patch, targetCollectionName));
			return result;
		});
	};

export const syncPartLabelsForSupplier = syncPartLabelsForRole(ROLE_TYPES.SUPPLIER);
